/**
 * CustomerTryOnEntry - Entry page for fixed in-store QR code link
 * 
 * URL format: https://nanokart.ai/<partner_id>/customers/tryon
 * 
 * Flow:
 * 1. Validate partner link
 * 2. Customer enters phone number + optional email
 * 3. OTP verification
 * 4. Redirect to session URL
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, ArrowRight, Loader2, CheckCircle, Store, 
  Shield, AlertCircle, RefreshCw, Mail, User
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerTryOnEntry = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  
  // Page state
  const [step, setStep] = useState('loading'); // 'loading' | 'phone' | 'otp' | 'success' | 'error'
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [phone, setPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const otpRefs = useRef([]);

  // Validate partner on load
  useEffect(() => {
    const validatePartner = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public-studio/v2/validate/${partnerId}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorMessage = 'Invalid store link';
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // If not JSON, use default message
          }
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        setPartnerInfo(data);
        setStep('phone');
      } catch (err) {
        console.error('Validation error:', err);
        setError(err.message || 'Invalid store link');
        setStep('error');
      }
    };
    
    if (partnerId) {
      validatePartner();
    } else {
      setError('Invalid store link');
      setStep('error');
    }
  }, [partnerId]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Handle phone submission
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    // Validate email if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public-studio/v2/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, partner_id: partnerId })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to send OTP';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default
        }
        throw new Error(errorMessage);
      }
      
      toast.success('OTP sent to your phone!');
      setStep('otp');
      setResendTimer(30);
      
      // Focus first OTP input
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error('Send OTP error:', err);
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when complete
    if (newOtp.every(d => d) && newOtp.join('').length === 4) {
      handleOtpSubmit(newOtp.join(''));
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP verification
  const handleOtpSubmit = async (otpValue) => {
    const otpCode = otpValue || otp.join('');
    
    if (otpCode.length !== 4) {
      toast.error('Please enter the complete 4-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public-studio/v2/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone, 
          otp: otpCode, 
          partner_id: partnerId,
          customer_name: customerName.trim() || undefined,
          email: email || undefined
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Invalid OTP';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // If server returns non-JSON error
          console.error('Non-JSON error response:', errorText);
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Store session info
      sessionStorage.setItem('instore_session', JSON.stringify({
        session_id: data.session_id,
        session_token: data.session_token,
        partner_id: partnerId,
        partner_name: data.partner_name,
        partner_logo_url: data.partner_logo_url,
        customer_name: data.customer_name || customerName.trim(),
        expires_at: data.expires_at,
        max_tryons: data.max_tryons,
        tryons_used: data.tryons_used,
        customer_email: email || null
      }));
      
      setStep('success');
      
      // Show message based on new or existing session
      if (data.is_existing_session) {
        toast.success('Welcome back! Resuming your session...');
      } else {
        toast.success('Session started! Enjoy trying on clothes.');
      }
      
      // Redirect to session page
      setTimeout(() => {
        navigate(`/${partnerId}/session/${data.session_id}`);
      }, 1500);
      
    } catch (err) {
      console.error('Verify OTP error:', err);
      toast.error(err.message || 'Verification failed');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public-studio/v2/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, partner_id: partnerId })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to resend OTP';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default
        }
        throw new Error(errorMessage);
      }
      
      toast.success('OTP resent!');
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 animate-spin mx-auto" />
          <p className="text-slate-500 mt-4 text-sm sm:text-base">Loading store...</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 rounded-2xl p-6 sm:p-8 max-w-md w-full text-center border border-red-200"
        >
          <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">Link Not Valid</h1>
          <p className="text-slate-600 text-sm sm:text-base">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header - Partner Branding (Logo + Name) */}
      <div className="p-4 flex items-center justify-center border-b border-slate-100">
        <div className="flex items-center gap-3">
          {partnerInfo?.partner_logo_url && (
            <img 
              src={partnerInfo.partner_logo_url} 
              alt={partnerInfo.partner_name}
              className="h-10 sm:h-12 object-contain"
            />
          )}
          {partnerInfo?.partner_name && (
            <span className="font-semibold text-slate-800 text-base sm:text-lg">{partnerInfo.partner_name}</span>
          )}
          {!partnerInfo?.partner_logo_url && !partnerInfo?.partner_name && (
            <Store className="w-6 h-6 text-blue-600" />
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Card */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl">
            
            {/* Header with Logo */}
            <div className="text-center mb-6 sm:mb-8">
              {/* TryOn Studio Logo */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260128073408.webp"
                  alt="Try-On Studio"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-1 sm:mb-2">
                Try-On Studio
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm">
                Try on clothes virtually before you buy
              </p>
            </div>

            <AnimatePresence mode="wait">
              {/* Phone Step */}
              {step === 'phone' && (
                <motion.form
                  key="phone"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onSubmit={handlePhoneSubmit}
                  className="space-y-3 sm:space-y-4"
                >
                  {/* Name Input */}
                  <div>
                    <label className="block text-slate-700 text-xs sm:text-sm mb-1.5 sm:mb-2 font-medium">
                      Your name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Enter your name"
                        data-testid="customer-name-input"
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Phone Input */}
                  <div>
                    <label className="block text-slate-700 text-xs sm:text-sm mb-1.5 sm:mb-2 font-medium">
                      Phone number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit mobile number"
                        data-testid="customer-phone-input"
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base tracking-wider"
                      />
                    </div>
                  </div>

                  {/* Email Input (Optional) */}
                  <div>
                    <label className="block text-slate-700 text-xs sm:text-sm mb-1.5 sm:mb-2 font-medium">
                      Email <span className="text-slate-400">(optional)</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        data-testid="customer-email-input"
                        className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      />
                    </div>
                    <p className="text-slate-400 text-[10px] sm:text-xs mt-1">
                      We&apos;ll send your try-on images here
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={!customerName.trim() || phone.length !== 10 || loading}
                    data-testid="customer-send-otp-btn"
                    className="w-full py-2.5 sm:py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 text-sm sm:text-base mt-2"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}

              {/* OTP Step */}
              {step === 'otp' && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5 sm:space-y-6"
                >
                  <div className="text-center">
                    <p className="text-slate-600 text-xs sm:text-sm mb-1">
                      Enter the 6-digit code sent to
                    </p>
                    <p className="text-slate-800 font-semibold text-sm sm:text-base">
                      +91 {phone.slice(0, 5)} {phone.slice(5)}
                    </p>
                  </div>

                  {/* OTP Input */}
                  <div className="flex justify-center gap-1.5 sm:gap-2">
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => otpRefs.current[idx] = el}
                        type="text"
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        data-testid={`customer-otp-input-${idx}`}
                        className="w-10 h-12 sm:w-12 sm:h-14 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 text-center text-lg sm:text-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength={1}
                      />
                    ))}
                  </div>

                  {/* Verify Button */}
                  <button
                    onClick={() => handleOtpSubmit()}
                    disabled={otp.join('').length !== 4 || loading}
                    data-testid="customer-verify-otp-btn"
                    className="w-full py-3 sm:py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 text-sm sm:text-base"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      <>
                        Verify & Start
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      </>
                    )}
                  </button>

                  {/* Resend & Change Number */}
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <button
                      onClick={() => {
                        setStep('phone');
                        setOtp(['', '', '', '']);
                      }}
                      className="text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Change number
                    </button>
                    <button
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || loading}
                      className="text-blue-600 hover:text-blue-700 disabled:text-slate-400 transition-colors flex items-center gap-1"
                    >
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Success Step */}
              {step === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6 sm:py-8"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                  >
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </motion.div>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">You&apos;re In!</h2>
                  <p className="text-slate-500 text-sm">Redirecting to your try-on studio...</p>
                  <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 animate-spin mx-auto mt-3 sm:mt-4" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Privacy Footer */}
      <div className="px-4 pb-6 sm:pb-8">
        <div className="max-w-md mx-auto">
          <div className="bg-slate-50 rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-slate-200">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              <h3 className="text-slate-800 font-semibold text-xs sm:text-sm">Your Privacy is Protected</h3>
            </div>
            <p className="text-slate-500 text-[10px] sm:text-xs leading-relaxed">
              Your photos remain private and visible only to you. Store cannot access your images. All data is auto-deleted after the session expires.
            </p>
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-200 flex items-center justify-center gap-2">
              <span className="text-slate-400 text-[10px] sm:text-xs">Powered by</span>
              <div className="flex items-center gap-1.5">
                {/* Nanokart Logo */}
                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M4 20V4l16 16V4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] sm:text-xs font-bold text-slate-700 leading-tight">Nanokart.ai</span>
                  <span className="text-[7px] sm:text-[8px] text-slate-400 leading-tight">Buy certain. Sell smarter.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTryOnEntry;
